import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Square, Sparkles, Brain, Clock, Building, Scale, Calendar } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useTutorial } from "@/hooks/useTutorial";
import { supabase } from "@/integrations/supabase/client";
import { NEW_APP_URL } from "@/config/retired";

interface CaseSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  falloId: string;
  caratula: string;
  tribunal?: string;
  expediente?: string;
  fecha?: string;
  tipo?: string; // "camara" or "csjn"
  downloadLink?: string; // Add the actual download link
  collection?: string; // Collection name for the court parameter
}

interface SummaryData {
  hechos_caso?: string;
  fundamentos_sentencia_apelada?: string;
  argumentos_parte_recurrente?: string;
  argumentos_contraparte?: string;
  decision_camara?: string;
  disidencias?: string;
  argumentos_camara?: string;
  votos_concurrentes?: string;
  validacion?: boolean;
}

export function CaseSummaryModal({ isOpen, onClose, falloId, caratula, tribunal, expediente, fecha, tipo, downloadLink, collection }: CaseSummaryModalProps) {
  const { session, user, subscription } = useAuth();
  const { currentStep, completeStep } = useTutorial();
  const [isLoading, setIsLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Mock summary data
  const mockSummaryData: SummaryData = {
    hechos_caso: "El actor FERNANDEZ, Daniel Alfredo demandó por accidente - acción civil a BENASSI S.A., EMPRESA DISTRIBUIDORA Y COMERCIALIZADORA NORTE S.A. (EDENOR) y GALENO ASEGURADORA DE RIESGOS DEL TRABAJO S.A. reclamando $260.000. Alegó haber ingresado a prestar tareas el 20/10/2008 y egresado por despido indirecto el 04/02/2010, con categoría medio oficial, realizando conexiones y reparaciones de cableado eléctrico en la vía pública, jornada de lunes a viernes 8 a 19 y sábados 8 a 13, remuneración $1.900. Relata que el 12/08/2009, alrededor de las 17 hs, estando sobre una escalera apoyada en un pilar para instalar un medidor, la escalera se rompió y cayó desde dos metros, golpeándose y produciéndose una lesión en la columna. Denunció falta de medidas de seguridad, informó el siniestro a su empleador y fue llevado a una clínica; luego se atendió por su obra social. Denunció el accidente mediante TCL y afirma que la demandada rechazó el siniestro. Reclama daño físico y psicológico con fundamento en derecho común (arts. 1109, 1113 y 1074 CC) y plantea la inconstitucionalidad del art. 39 de la ley 24.557.",
    fundamentos_sentencia_apelada: null,
    argumentos_parte_recurrente: null,
    argumentos_contraparte: null,
    decision_camara: "Sentencia de primera instancia: se rechazó en todas sus partes la demanda interpuesta por FERNANDEZ, Daniel Alfredo contra EMPRESA DISTRIBUIDORA Y COMERCIALIZADORA NORTE S.A. (EDENOR) y GALENO ASEGURADORA DE RIESGOS DEL TRABAJO S.A.; se impusieron las costas en el orden causado; se reguló honorarios y honorarios del perito (se consignan UMAs y montos en la resolución).",
    disidencias: null,
    argumentos_camara: "El juez valoró la prueba conforme al artículo 377 del CPCCN. Se produjo peritaje médico-legista (Dr. Rubén Roberto Frontini) fechado 16/09/2020, cuyo informe concluyó que el Sr. Fernández no presenta incapacidad física por el motivo de la litis; la RMN y el examen clínico no mostraron alteraciones en columna lumbar. Se solicitó además estudio psicodiagnóstico; la parte actora fue intimada para realizarlo y, ante la inacción del actor, fue tenido por desistido de la pericia psicológica y por renuente respecto de la pericia de la contraria. En esas condiciones el tribunal otorgó pleno valor probatorio al peritaje médico (cfr. arts. 386 y 477 CPCCN) y concluyó inexistencia de la patología psicofísica denunciada y ausencia de prueba de incapacidad actual vinculada al accidente (cfr. art. 1068 CC). Por ausencia de prueba del daño resarcible se rechazó la demanda; en consecuencia resultó innecesario analizar los restantes planteos (incluyendo la cuestión de constitucionalidad y eventual responsabilidad de la aseguradora). Se distribuyeron las costas en el orden causado (art. 68 CPCCN) y se regularon honorarios y remuneraciones del perito conforme lo consignado en la sentencia.",
    votos_concurrentes: null,
    validacion: true
  };

  const sectionTitles: Record<string, string> = {
    hechos_caso: "Hechos del Caso",
    fundamentos_sentencia_apelada: "Fundamentos de la Sentencia Apelada",
    argumentos_parte_recurrente: "Argumentos de la Parte Recurrente",
    argumentos_contraparte: "Argumentos de la Contraparte",
    decision_camara: "Decisión de la Cámara",
    argumentos_camara: "Argumentos de la Cámara",
    disidencias: "Disidencias",
    votos_concurrentes: "Votos Concurrentes"
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const saveSummaryToHistory = async (summaryData: SummaryData) => {
    try {
      const { error } = await supabase
        .from('summary_history')
        .insert({
          user_id: user?.id || '',
          case_title: caratula,
          case_metadata: {
            carátula: caratula,
            tribunal,
            expediente,
            fecha,
            falloId,
            tipo
          } as any,
          summary_data: summaryData as any
        });

      if (error) {
        console.error('Error saving summary to history:', error);
      }
    } catch (error) {
      console.error('Error saving summary to history:', error);
    }
  };

  const generateSummary = async () => {
    // Retired build: summaries are generated, and charged, only in the new app.
    toast({
      title: "Los resúmenes están en la nueva versión",
      description: `Generalos en ${NEW_APP_URL.replace("https://", "")}; esta versión queda solo para consulta.`,
    });
    onClose();
  };

  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const resetModal = () => {
    setIsLoading(false);
    setSummaryData(null);
    setTimeElapsed(0);
    stopTimer();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  useEffect(() => {
    console.log('Modal effect triggered:', { isOpen, summaryData, isLoading });
    console.log('Condition check:', {
      isOpen,
      hasSummaryData: !!summaryData,
      isLoading,
      shouldStart: isOpen && !summaryData && !isLoading
    });
    
    if (isOpen && !summaryData && !isLoading) {
      console.log('Starting summary generation...');
      generateSummary();
    }
    
    if (!isOpen) {
      console.log('Modal closed, resetting...');
      resetModal();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      stopTimer();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const LoadingSkeleton = () => (
    <div className="space-y-8 pb-20">
      {Object.entries(sectionTitles).map(([key, title], index) => (
        <div key={key} className="group p-6 bg-gradient-surface rounded-xl border border-border/20 hover:border-primary/30 transition-all duration-500 relative overflow-hidden">
          {/* Subtle hover effect */}
          <div className="absolute inset-0 bg-gradient-primary opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-primary rounded-lg shadow-glow group-hover:shadow-intense transition-all duration-300">
                <Brain className="h-4 w-4 text-primary-foreground" />
              </div>
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                {title}
              </h3>
            </div>
            
            <div className="text-foreground/90 leading-relaxed whitespace-pre-line text-sm pl-11 space-y-3">
              <div className="space-y-2">
                <Skeleton className="h-4 w-full bg-foreground/20 dark:bg-muted/60" />
                <Skeleton className="h-4 w-full bg-foreground/20 dark:bg-muted/60" />
                <Skeleton className="h-4 w-5/6 bg-foreground/20 dark:bg-muted/60" />
                {index % 3 === 0 && <Skeleton className="h-4 w-4/5 bg-foreground/20 dark:bg-muted/60" />}
                {index % 2 === 1 && (
                  <>
                    <Skeleton className="h-4 w-full bg-foreground/20 dark:bg-muted/60" />
                    <Skeleton className="h-4 w-3/4 bg-foreground/20 dark:bg-muted/60" />
                  </>
                )}
                <Skeleton className="h-4 w-2/3 bg-foreground/20 dark:bg-muted/60" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderSummaryContent = () => {
    if (!summaryData) return null;

    return (
      <div className="space-y-8 pb-20">
        {Object.entries(sectionTitles).map(([key, title]) => {
          // Skip validacion as requested
          if (key === 'validacion') return null;
          
          const content = summaryData[key as keyof SummaryData];
          const displayContent = (!content || content === null) ? "-" : content;

          return (
            <div key={key} className="group p-6 bg-gradient-surface rounded-xl border border-border/20 hover:border-primary/30 transition-all duration-500 relative overflow-hidden">
              {/* Subtle hover effect */}
              <div className="absolute inset-0 bg-gradient-primary opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-primary rounded-lg shadow-glow group-hover:shadow-intense transition-all duration-300">
                    <Brain className="h-4 w-4 text-primary-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                    {title}
                  </h3>
                </div>
                
                <div className="text-foreground/90 leading-relaxed whitespace-pre-line text-sm pl-11 space-y-3">
                  {displayContent === "-" ? (
                    <p className="text-muted-foreground italic transition-all duration-300">
                      {displayContent}
                    </p>
                  ) : typeof displayContent === 'string' ? (
                    displayContent.split('\n').map((paragraph, idx) => (
                      paragraph.trim() && (
                        <p key={idx} className="transition-all duration-300 hover:text-foreground">
                          {paragraph}
                        </p>
                      )
                    ))
                  ) : (
                    <p className="transition-all duration-300 hover:text-foreground">
                      {String(displayContent)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const LoadingFooter = () => (
    <div className="fixed bottom-0 left-0 right-0 p-3 bg-gradient-to-r from-card/95 via-card to-card/95 backdrop-blur-xl border-t border-border/30 z-[60]">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="animate-spin rounded-full h-3 w-3 border border-primary/30 border-t-primary"></div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-2.5 w-2.5" />
            <span>{timeElapsed}s</span>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={stopGeneration}
          className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive transition-all duration-300"
        >
          <Square className="h-2.5 w-2.5" />
        </Button>
      </div>
    </div>
  );

  console.log('Rendering modal with:', { isOpen, isLoading, hasSummaryData: !!summaryData });
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4 mb-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <DialogTitle className="text-xl font-semibold">
                  Resumen Inteligente del Fallo
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground mt-1">
                  {caratula}
                </DialogDescription>
                {/* Metadata */}
                {(tribunal || expediente || fecha) && (
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-2">
                    {tribunal && (
                      <div className="flex items-center gap-1">
                        <Building className="h-3 w-3" />
                        <span>{tribunal}</span>
                      </div>
                    )}
                    {expediente && (
                      <div className="flex items-center gap-1">
                        <Scale className="h-3 w-3" />
                        <span>{expediente}</span>
                      </div>
                    )}
                    {fecha && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>{fecha}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-hidden relative">
            <ScrollArea className="h-full w-full">
              <div className="pr-4 pb-16">
                {isLoading ? <LoadingSkeleton /> : renderSummaryContent()}
              </div>
            </ScrollArea>
          </div>

          {/* Footer inside modal - only show when loading */}
          {isLoading && (
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-card/95 backdrop-blur-xl border-t border-border/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-3 w-3 border border-primary/30 border-t-primary"></div>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-2.5 w-2.5" />
                      <span>{timeElapsed}s</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Generando resumen... (Demanda aproximadamente 30-45s, por favor tené paciencia!)
                    </span>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={stopGeneration}
                  className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive transition-all duration-300"
                >
                  <Square className="h-2.5 w-2.5" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}